import type { UnitEconomicsResponse, UnitEconomicsUserRow } from "@voice2mail/shared/v2m_api";

import { useSortableRows } from "../hooks/useSortableRows";
import { MetricCard } from "./MetricCard";
import { SortableTh } from "./SortableTh";

interface UnitEconomicsProps {
  data: UnitEconomicsResponse;
}

type TopUserSortKey = "userId" | "tier" | "generations" | "tokensIn" | "tokensOut" | "costUsd";
const ACCESSORS: Record<TopUserSortKey, (r: UnitEconomicsUserRow) => string | number> = {
  userId: (r) => r.userId,
  tier: (r) => r.tier,
  generations: (r) => r.generations,
  tokensIn: (r) => r.tokensIn,
  tokensOut: (r) => r.tokensOut,
  costUsd: (r) => r.costUsd,
};

const TD = "px-4 py-3 text-sm text-slate-900";

function TopUsersTable({ rows }: { rows: UnitEconomicsUserRow[] }) {
  // Default: costliest customer first — the thing this table exists to surface.
  const { sorted, sort, toggleSort } = useSortableRows(rows, ACCESSORS, {
    key: "costUsd",
    direction: "desc",
  });

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <SortableTh label="User" sortKey="userId" sort={sort} onSort={toggleSort} sticky />
              <SortableTh label="Tier" sortKey="tier" sort={sort} onSort={toggleSort} />
              <SortableTh label="Generacje" sortKey="generations" sort={sort} onSort={toggleSort} />
              <SortableTh label="Tokeny in" sortKey="tokensIn" sort={sort} onSort={toggleSort} />
              <SortableTh label="Tokeny out" sortKey="tokensOut" sort={sort} onSort={toggleSort} />
              <SortableTh label="Koszt (USD)" sortKey="costUsd" sort={sort} onSort={toggleSort} />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {sorted.map((r) => (
              <tr key={r.userId} className="group hover:bg-slate-50 transition-colors">
                <td
                  className={`${TD} sticky left-0 z-10 bg-white group-hover:bg-slate-50 font-mono text-xs`}
                >
                  {r.userId}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">{r.tier}</td>
                <td className={TD}>{r.generations.toLocaleString()}</td>
                <td className={TD}>{r.tokensIn.toLocaleString()}</td>
                <td className={TD}>{r.tokensOut.toLocaleString()}</td>
                <td className={`${TD} font-medium`}>${r.costUsd.toFixed(4)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * V2M-DEV-163 postawił tu bursztynowe ostrzeżenie zamiast wykresu samych zer,
 * bo `UnitEconomicsResponse` nie niósł wtedy ŻADNEGO pola all-time: puste
 * `topUsers` + puste `costByTier` było na drucie nieodróżnialne od „pipeline
 * `daily_user_metrics` nigdy nie zapisał ani jednego dokumentu". Odmowa
 * narysowania zer była słuszna — brakowało sygnału, nie wykresu.
 *
 * V2M-DEV-165 dołożył ten sygnał (`hoursSinceLastEventEver`, odczyt ALL-TIME,
 * nieprzycięty oknem `days`), V2M-DEV-167 domyka pętlę tutaj: ostrzeżenie o
 * niejednoznaczności znika, bo niejednoznaczności już nie ma.
 *
 * `null` = kolekcja `daily_user_metrics` NIGDY nie dostała zapisu — to nie
 * jest zero, to brak pomiaru, i renderuje się na czerwono (ten sam kontrakt i
 * ta sama forma co `DailyMetricsFreshness`). Liczba = pipeline żyje i pisał
 * tyle godzin temu, choćby okno było puste — wtedy zero na wykresie JEST
 * odczytem, nie ciszą.
 */
export function UnitEconomics({ data }: UnitEconomicsProps) {
  const tierEntries = Object.entries(data.costByTier);
  const neverMeasured = data.hoursSinceLastEventEver === null;
  const emptyWindow = data.topUsers.length === 0 && tierEntries.length === 0;

  if (neverMeasured) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
        <div className="text-red-800 text-sm font-medium">
          NIGDY nie zapisano do <code>daily_user_metrics</code> — ani razu, w całej historii, nie
          tylko w ostatnie {data.days} {data.days === 1 ? "dobę" : "dni"}. To nie jest zero, to brak
          pomiaru.
        </div>
      </div>
    );
  }

  const freshness = (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:max-w-2xl">
      <MetricCard
        label="Godzin od ostatniego pomiaru (kiedykolwiek)"
        value={data.hoursSinceLastEventEver!.toFixed(1)}
      />
    </div>
  );

  if (emptyWindow) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          Zero zużycia w ostatnie {data.days} {data.days === 1 ? "dobę" : "dni"} — zmierzone, nie
          domyślone: pipeline <code>daily_user_metrics</code> żyje i pisał poniżej podaną liczbę
          godzin temu.
        </p>
        {freshness}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {freshness}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:max-w-2xl">
        <MetricCard
          label="Wypalanie darmowego tieru (USD)"
          value={`$${data.freeTierBurnUsd.toFixed(4)}`}
        />
        {tierEntries.map(([tier, bucket]) => (
          <MetricCard
            key={tier}
            label={`${tier} — koszt (USD)`}
            value={`$${bucket.costUsd.toFixed(4)}`}
          />
        ))}
      </div>
      {tierEntries.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:max-w-2xl">
          {tierEntries.map(([tier, bucket]) => (
            <MetricCard
              key={`${tier}-users`}
              label={`${tier} — użytkownicy`}
              value={bucket.users}
            />
          ))}
        </div>
      )}
      {data.topUsers.length > 0 && <TopUsersTable rows={data.topUsers} />}
    </div>
  );
}
